import { useQuery } from "@tanstack/react-query";
import { getLessonWithLatestVersions } from "api/services/lesson-service";
import { IGetLessonWithVersionsResponse } from "api/models/response/IGetLessonWithVersionsResponse";
import { LessonType } from "models/LessonType";
import type { LessonVersion, VrLessonVersion, GameLessonVersion } from "models/LessonVersion";
import { useAuthContext } from "hooks/useAuthContext";

export const LESSON_WITH_VERSIONS_QUERY_KEY = ["lesson-with-versions"] as const;

function normalizeEvents(events: unknown): GameLessonVersion["events"] {
    if (!Array.isArray(events)) return [];
    return events.map((e) => {
        if (typeof e === "object" && e !== null && "eventKey" in e && "points" in e) {
            return { eventKey: String((e as { eventKey: unknown }).eventKey), points: Number((e as { points: unknown }).points) || 0 };
        }
        return { eventKey: "", points: 0 };
    }).filter((e) => e.eventKey.trim() !== "");
}

function normalizeVersion(v: LessonVersion, type: LessonType): LessonVersion {
    if (type === LessonType.GAME) {
        const game = v as Partial<GameLessonVersion>;
        return {
            ...v,
            type: LessonType.GAME,
            language: game.language ?? "",
            name: game.name ?? "",
            description: game.description ?? null,
            fileId: game.fileId ?? "",
            launchParams: game.launchParams ?? "",
            events: normalizeEvents(game.events),
            isDefault: game.isDefault ?? false,
        } as GameLessonVersion;
    }
    const vr = v as Partial<VrLessonVersion>;
    return {
        ...v,
        type: LessonType.VR,
        language: vr.language ?? "",
        name: vr.name ?? "",
        description: vr.description ?? null,
        nodesConfiguration: vr.nodesConfiguration ?? null,
        isDefault: vr.isDefault ?? false,
    } as VrLessonVersion;
}

export const useGetLessonWithLatestVersions = (lessonId: string) => {
    const { userState } = useAuthContext();
    const workspaceId = userState.user.selectedWorkspace.workspaceId;

    const query = useQuery({
        queryKey: [...LESSON_WITH_VERSIONS_QUERY_KEY, lessonId, workspaceId],
        queryFn: async (): Promise<IGetLessonWithVersionsResponse | null> => {
            if (!lessonId || !workspaceId) return null;
            const response = await getLessonWithLatestVersions({ lessonId, workspaceId });
            if (!response.success || !response.data) return null;
            const lessonType = response.data.lessonType ?? LessonType.VR;
            return {
                ...response.data,
                versions: response.data.versions.map((v) => normalizeVersion(v, lessonType)),
            };
        },
        enabled: !!lessonId && !!workspaceId,
    });

    return {
        lessonData: query.data ?? null,
        fetchLessonWithLatestVersions: query.refetch,
        loading: query.isLoading || query.isFetching,
    };
};
